import httpClient from '@/lib/httpClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface MarketingWording {
  version: number
  text: string
}

/**
 * The marketing consent wording (public). Show `text` next to every opt-in
 * box and send `version` back with the choice: the API logs that exact text
 * as proof of consent.
 */
export function useMarketingWording() {
  return useQuery<MarketingWording>({
    queryKey: ['marketing-wording'],
    queryFn: async () => {
      const response = await httpClient.get('auth/marketing-consent')
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Records the choice from the post-social-sign-up screen or the one-time
 * in-app prompt ("no" is recorded too), then refreshes the current user so
 * the screen / prompt goes away.
 */
export function useSaveMarketingChoice() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (values: {
      optedIn: boolean
      source: 'social' | 'in_app_prompt'
      wordingVersion: number
    }) => {
      const response = await httpClient.post('auth/user/marketing-consent', {
        opted_in: values.optedIn,
        source: values.source,
        wording_version: values.wordingVersion,
      })
      return response.data
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['current-user'] }),
    // An old wording version means the text changed under the user: show the new one.
    onError: () =>
      queryClient.invalidateQueries({ queryKey: ['marketing-wording'] }),
  })

  return { saveChoice: mutation.mutateAsync, saveChoiceStatus: mutation }
}
